// ViewLocations.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table, Modal, Pagination, message } from "antd";
import { UserContext } from "../UserContext";
import { withRouter } from "../utils/withRouter";
import api from "../api/index";
import "./styles/TransferTable.css";

function TransferTable() {
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [subagents, setSubagents] = useState([]);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sorter, setSorter] = useState({ field: null, order: null }); // order: 'ascend' | 'descend' | null

  useEffect(() => {
    if (!user?.id || !user?.access_token) return;

    const load = async () => {
      try {
        const [res, transferRes] = await Promise.all([
          fetch(
            `${api.main}api/agent/all_locations?id=${user.id}&access_token=${user.access_token}`
          ),
          fetch(
            `${api.main}api/agent/transfers?sagent_id=${user.id}&access_token=${user.access_token}`
          ),
        ]);

        if (!res.ok) {
          throw new Error("Locations: Not authorized or server error");
        }

        const data = await res.json();

        let transferringIds = new Set();

        if (transferRes.ok) {
          const transferData = await transferRes.json();
          console.log("transferData from API:", transferData);

          const allTransfers = Array.isArray(transferData.transfers)
            ? transferData.transfers
            : [];

          // Only treat locations as "transferring" if the transfer is still pending
          // and not expired. Once a transfer is cancelled/denied/accepted/etc,
          // those locations are no longer blocked.
          const activeTransfers = allTransfers.filter(
            (t) => t.status === "pending" && !t.is_expired
          );

          const idList = activeTransfers
            .flatMap((t) => {
              if (Array.isArray(t.location_ids)) {
                return t.location_ids;
              }

              if (t.location_id != null) {
                return [t.location_id];
              }

              if (t.location_registration_id != null) {
                return [t.location_registration_id];
              }
              if (t.registration_id != null) {
                return [t.registration_id];
              }
              if (t.location && t.location.registration_id != null) {
                return [t.location.registration_id];
              }

              // Nothing usable for this transfer
              return [];
            })
            .filter((id) => id != null)
            .map((id) => String(id));

          transferringIds = new Set(idList);
        } else {
          const errText = await transferRes.text();
          console.warn(
            "Failed to load transfer IDs:",
            transferRes.status,
            errText
          );
        }

        if (data.message) {
          setSubagents([]);
        } else {
          const formatted = (data.data || []).map((s) => {
            const isTransferring = transferringIds.has(String(s.registration_id));
            const baseStatusLabel = s.status === "A" ? "Active" : "Inactive";

            const line1 = s.address || "";
            const line2Parts = [
              s.city,
              s.state && s.zipcode ? `${s.state} ${s.zipcode}` : s.state || "",
              s.country,
            ].filter(Boolean);
            const line2 = line2Parts.join(", ");
            const fullAddress = [line1, line2].filter(Boolean).join(", ");

            return {
              ...s,
              address: fullAddress, 
              isTransferring,
              statusLabel: isTransferring ? "Transferring" : baseStatusLabel,
              actionLabel: s.status === "A" ? "Deactivate" : "Activate",
            };
          });

          // Hide any locations that are currently transferring
          const visibleLocations = formatted.filter(
            (row) => !row.isTransferring
          );

          setSubagents(visibleLocations);
        }
      } catch (err) {
        setError(err.message || "Failed to load locations");
        message.error(err.message || "Failed to load locations");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.id, user?.access_token]);

  // search + paging
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrent(1);
  };

  const searchedLocations = useMemo(() => {
    const q = (searchQuery || "").toLowerCase().trim();
    if (!q) return subagents;
    const tokens = q.split(/\s+/).filter(Boolean);
    return subagents.filter((l) => {
      const hay = [l.business_name, l.address, l.city, l.state, l.zipcode]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [subagents, searchQuery]);

  const sortedLocations = useMemo(() => {
    const list = [...searchedLocations];
    const { field, order } = sorter || {};
    if (!field || !order) return list;

    const dir = order === "ascend" ? 1 : -1;

    const getVal = (row, key) => {
      switch (key) {
        case "business_name":
        case "address":
          return (row[key] || "").toString().toLowerCase();
        case "vatm_fee":
        case "daily_cash_limit":
          return Number(row[key] ?? 0);
        case "status":
          // A = Active (1), else 0
          return row.status === "A" ? 1 : 0;
        default:
          return row[key];
      }
    };

    list.sort((a, b) => {
      const av = getVal(a, field);
      const bv = getVal(b, field);
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return list;
  }, [searchedLocations, sorter]);

  const { pageRows, total } = useMemo(() => {
    const start = (current - 1) * pageSize;
    return {
      pageRows: sortedLocations.slice(start, start + pageSize),
      total: sortedLocations.length,
    };
  }, [sortedLocations, current, pageSize]);

  const initiateTransfer = () => {
    const ids = selectedRowKeys;
    if (!ids.length) return;
    let email = "";

    Modal.confirm({
      title: `Transfer ${ids.length} location${
        ids.length > 1 ? "s" : ""
      }?`,
      content: (
        <>
          <label>Send transfer invitation to this email</label>
          <input
            type="email"
            placeholder="Agent email..."
            defaultValue=""
            onChange={(e) => (email = e.target.value)}
            style={{
              width: "80%",
              marginTop: 8,
              border: "2px solid var(--light-blue)",
              borderRadius: "12px",
              padding: "5px 5px",
            }}
          />
          <p
            style={{
              marginTop: 10,
              color: "#667085",
              fontSize: 13,
              fontStyle: "italic",
            }}
          >
            If there is an assigned license for this location, it will be
            unassigned after the transfer is complete.
          </p>
        </>
      ),
      okButtonProps: { className: "btn btn-primary" },
      okText: "Transfer",
      cancelText: "Cancel",
      centered: true,

      // custom footer to keep buttons on one line
      footer: (_, { OkBtn, CancelBtn }) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <CancelBtn />
          <OkBtn />
        </div>
      ),

      onOk: async () => {
        // simple validation
        if (!/\S+@\S+\.\S+/.test(email)) {
          message.error("Please enter a valid email.");
          // DO NOT reject; just return to let the promise resolve
          return;
        }

        try {
          const response = await fetch(
            `${api.main}api/agent/transfer_location`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                access_token: user.access_token,
                sagent_id: user.id,
                location_ids: ids,
                to_sagent_email: email,
              }),
            }
          );

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || "An error occurred");
          }

          // success
          setSubagents((prev) =>
            prev.filter((s) => !ids.includes(s.registration_id))
          );
          setSelectedRowKeys([]);
          message.success(
            `Transferred ${ids.length} item${
              ids.length > 1 ? "s" : ""
            }`
          );
        } catch (err) {
          console.error("Error:", err);
          message.error(err.message || "Transfer failed.");
        }
      },
    });
  };

  const getStatusMeta = (row) => {
    if (row.isTransferring) {
      return { label: "Transferring", variant: "transfer" };
    }
    if (row.status === "A") {
      return { label: "Active", variant: "active" };
    }
    return { label: "Inactive", variant: "inactive" };
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "business_name",
      key: "business_name",
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "business_name" ? sorter.order : null,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "address" ? sorter.order : null,
      responsive: ["sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "Fee",
      dataIndex: "vatm_fee",
      key: "vatm_fee",
      render: (v) => `$${Number(v || 0).toFixed(2)}`,
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "vatm_fee" ? sorter.order : null,
      responsive: ["md", "lg", "xl", "xxl"],
    },
    {
      title: "Daily Limit",
      dataIndex: "daily_cash_limit",
      key: "daily_cash_limit",
      render: (v) => `$${Number(v || 0).toFixed(2)}`,
      align: "left",
      sorter: true,
      sortOrder:
        sorter.field === "daily_cash_limit" ? sorter.order : null,
      responsive: ["md", "lg", "xl", "xxl"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, row) => {
        const meta = getStatusMeta(row);
        return (
          <span
            className={`transfer-table-status status-pill status-pill--${meta.variant === "transfer" ? "danger" : meta.variant === "active" ? "success" : "neutral"}`}
          >
            {meta.label}
          </span>
        );
      },
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "status" ? sorter.order : null,
      responsive: ["sm", "md", "lg", "xl", "xxl"],
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      name: record.registration_id,
      disabled: record.isTransferring,
    }),
  };

  const pagerTotalForLayout = Math.max(1, total);
  const totalPages = Math.max(1, Math.ceil(pagerTotalForLayout / pageSize));
  const safeCurrent = Math.min(current, totalPages);

  return (
    <div className="transfer-table-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Transfers</p>
          <h2>Transfer Locations</h2>
          <p className="section-subtitle">
            Transfer any location. If a location is licensed, its license will
            be unassigned after the transfer is complete.
          </p>
        </div>
        <div className="page-pill">
          {selectedRowKeys.length
            ? `${selectedRowKeys.length} selected`
            : "No selections"}
        </div>
      </div>

      <section className="page-section transfer-table-panel">
        {loading && (
          <div className="transfer-table-loading">Loading merchants...</div>
        )}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="transfer-table-toolbar data-surface-toolbar">
          <div className="transfer-table-search">
            <label htmlFor="transfer-table-search">Search locations</label>
            <input
              id="transfer-table-search"
              type="text"
              placeholder="Search by name or address…"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="transfer-table-toolbar-actions">
            <div className="transfer-table-meta data-surface-meta">
              <span className="data-surface-meta__item">
                <strong>{total}</strong> ready location{total === 1 ? "" : "s"}
              </span>
              <span className="data-surface-meta__item">
                <strong>{selectedRowKeys.length}</strong> selected
              </span>
            </div>
            <button
              className="btn btn-primary"
              disabled={!selectedRowKeys.length}
              onClick={initiateTransfer}
            >
              Transfer Location(s)
            </button>
          </div>
        </div>

        <div className="transfer-table-wrapper show-scrollbar">
          <Table
            className="transfer-table-grid"
            size="medium"
            rowKey={(row) => row.registration_id}
            rowClassName={(row) =>
              row.isTransferring ? "row-transferring" : ""
            }
            columns={columns}
            dataSource={pageRows}
            loading={loading}
            pagination={false}
            tableLayout="auto"
            locale={{
              emptyText: (
                <div className="table-empty-state">
                  <strong>No transferable locations found</strong>
                  <span>Everything may already be transferring, or your search returned no matches.</span>
                </div>
              ),
            }}
            rowSelection={rowSelection}
            scroll={{ x: "max-content", y: 520 }}
            onChange={(_, __, tableSorter) => {
              const s = Array.isArray(tableSorter) ? tableSorter[0] : tableSorter;
              const next = {
                field: s?.columnKey || s?.field || null,
                order: s?.order || null,
              };
              setSorter(next);
            }}
            sticky
          />
        </div>

        <div className="transfer-table-footer data-surface-footer">
          <Pagination
            disabled={total === 0}
            className="transfer-table-pagination"
            current={safeCurrent}
            pageSize={pageSize}
            total={pagerTotalForLayout}
            showSizeChanger={false}
            showTotal={(t, range) =>
              total > 0 ? `${range[0]}-${range[1]} of ${total}` : "0-0 of 0"
            }
            onChange={(page, size) => {
              setCurrent(page);
              setPageSize(size);
            }}
          />
        </div>
      </section>
    </div>
  );
}

export default withRouter(TransferTable);
